import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, FormControl,
  FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { colorService, curvaTalleService, modeloService, temporadaService } from '../../../services/fashionService';
import { catalogosService } from '../../../services/catalogosService';
import { CurvaTalle, FashionColor, FashionModelo, MatrizModelo, ModeloFoto, Temporada } from '../../../types/fashion';
import { Clase, Linea, Ramo } from '../../../types';
import { GaleriaFotosSlot, GaleriaFotosHandle } from '../../../components/inventario/GaleriaFotosEditor';

const trimCode = (v?: string | null) => (v ?? '').trim();

const groupFotosByColor = (fotos: ModeloFoto[]) => {
  const byColor: Record<string, ModeloFoto[]> = {};
  for (const foto of fotos) {
    const color = trimCode(foto.codigoColor);
    if (!color) continue;
    (byColor[color] ??= []).push(foto);
  }
  return byColor;
};
const ModeloForm: React.FC = () => {
  const { codigoModelo } = useParams();
  const isEdit = Boolean(codigoModelo);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [clases, setClases] = useState<Clase[]>([]);
  const [curvas, setCurvas] = useState<CurvaTalle[]>([]);
  const [temporadas, setTemporadas] = useState<Temporada[]>([]);
  const [colores, setColores] = useState<FashionColor[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [matriz, setMatriz] = useState<MatrizModelo | null>(null);
  const [fotosPorColor, setFotosPorColor] = useState<Record<string, ModeloFoto[]>>({});
  const galeriaRefs = useRef<Record<string, GaleriaFotosHandle | null>>({});
  const [form, setForm] = useState<Omit<FashionModelo, 'cantidadVariantes' | 'nombreCurva' | 'nombreTemporada'>>({
    codigoModelo: '',
    descripcion: '',
    codigoRamo: '',
    codigoLinea: '',
    codigoClase: '',
    codigoCurva: '',
    codigoTemporada: '',
    precioBase: 0,
    costoBase: 0,
    estado: 'A',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [ramosRes, curvasRes, tempRes, coloresRes] = await Promise.all([
          catalogosService.getRamos(1, 1000),
          curvaTalleService.getCurvas(1, 1000),
          temporadaService.getTemporadas(1, 1000),
          colorService.getColores(1, 1000),
        ]);
        if (cancelled) return;

        setRamos((ramosRes.items || []).map((r) => ({
          ...r,
          codigoRamo: trimCode(r.codigoRamo),
          nombreRamo: (r.nombreRamo || '').trim(),
        })));
        setCurvas((curvasRes.items || []).map((c) => ({
          ...c,
          codigoCurva: trimCode(c.codigoCurva),
        })));
        setTemporadas((tempRes.items || []).map((t) => ({
          ...t,
          codigoTemporada: trimCode(t.codigoTemporada),
        })));
        setColores((coloresRes.items || [])
          .filter((c) => {
            const est = trimCode(c.estado).toUpperCase();
            return !est || est === 'A' || est === 'ACTIVO';
          })
          .map((c) => ({
            ...c,
            codigoColor: trimCode(c.codigoColor),
            nombreColor: (c.nombreColor || '').trim(),
            estado: trimCode(c.estado) || 'A',
          })));

        if (codigoModelo) {
          const data = await modeloService.getModelo(codigoModelo);
          if (cancelled) return;

          // Líneas/clases las cargan los effects de codigoRamo/codigoLinea (evita doble request).
          setForm({
            codigoModelo: trimCode(data.codigoModelo),
            descripcion: (data.descripcion || '').trim(),
            codigoRamo: trimCode(data.codigoRamo),
            codigoLinea: trimCode(data.codigoLinea),
            codigoClase: trimCode(data.codigoClase),
            codigoCurva: trimCode(data.codigoCurva),
            codigoTemporada: trimCode(data.codigoTemporada) || '',
            precioBase: data.precioBase ?? 0,
            costoBase: data.costoBase ?? 0,
            estado: trimCode(data.estado) || 'A',
          });

          const mat = await modeloService.getMatriz(codigoModelo);
          if (cancelled) return;
          setMatriz(mat);

          const fotos = await modeloService.getFotos(codigoModelo);
          if (cancelled) return;
          const byColor = groupFotosByColor(fotos);
          setFotosPorColor(byColor);

          const coloresMatriz = mat.celdas
            .filter((c) => c.existe)
            .map((c) => trimCode(c.codigoColor))
            .filter((v, i, a) => v && a.indexOf(v) === i);
          const coloresFotos = Object.keys(byColor);
          setSelectedColors(
            Array.from(new Set([...coloresMatriz, ...coloresFotos])),
          );
        }
      } catch {
        if (!cancelled) {
          enqueueSnackbar('Error al cargar datos del modelo', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [codigoModelo, enqueueSnackbar]);

  useEffect(() => {
    // En alta, o al cambiar ramo manualmente, recargar líneas
    if (loading) return;
    if (!form.codigoRamo) {
      setLineas([]);
      return;
    }
    let cancelled = false;
    catalogosService.getLineas(1, 1000, undefined, form.codigoRamo)
      .then((r) => {
        if (cancelled) return;
        setLineas((r.items || []).map((l) => ({
          ...l,
          codigoLinea: trimCode(l.codigoLinea),
          codigoRamo: trimCode(l.codigoRamo),
          nombreLinea: (l.nombreLinea || '').trim(),
        })));
      })
      .catch(() => {
        if (!cancelled) setLineas([]);
      });
    return () => { cancelled = true; };
  }, [form.codigoRamo, loading]);

  useEffect(() => {
    if (loading) return;
    if (!form.codigoRamo || !form.codigoLinea) {
      setClases([]);
      return;
    }
    let cancelled = false;
    catalogosService.getClases(1, 1000, undefined, form.codigoRamo, form.codigoLinea)
      .then((r) => {
        if (cancelled) return;
        setClases((r.items || []).map((c) => ({
          ...c,
          codigoClase: trimCode(c.codigoClase),
          codigoRamo: trimCode(c.codigoRamo),
          codigoLinea: trimCode(c.codigoLinea),
          nombreClase: (c.nombreClase || '').trim(),
        })));
      })
      .catch(() => {
        if (!cancelled) setClases([]);
      });
    return () => { cancelled = true; };
  }, [form.codigoRamo, form.codigoLinea, loading]);

  const tallesHeader = useMemo(
    () => (matriz?.talles || []).slice().sort((a, b) => a.orden - b.orden),
    [matriz],
  );

  const getCelda = (color: string, talle: string) =>
    matriz?.celdas.find((c) => c.codigoColor === color && c.codigoTalle === talle);

  const persistFotosModelo = async (codigo: string) => {
    for (const color of selectedColors) {
      const handle = galeriaRefs.current[color];
      if (!handle) continue;
      await handle.persist({
        deleteFoto: (fotoId) => modeloService.deleteFoto(codigo, fotoId),
        uploadFoto: (archivo, esPrincipal) =>
          modeloService
            .uploadFoto(codigo, { archivo, codigoColor: color, esPrincipal })
            .then(() => undefined),
        setFotoPrincipal: (fotoId) => modeloService.setFotoPrincipal(codigo, fotoId),
      });
    }

    const fotos = await modeloService.getFotos(codigo);
    const byColor = groupFotosByColor(fotos);
    setFotosPorColor(byColor);
    for (const color of selectedColors) {
      galeriaRefs.current[color]?.setFromServer(byColor[color] ?? []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codigoModelo.trim() || !form.descripcion.trim() || !form.codigoCurva) {
      enqueueSnackbar('Completá código, descripción y curva', { variant: 'warning' });
      return;
    }
    if (!form.codigoRamo || !form.codigoLinea || !form.codigoClase) {
      enqueueSnackbar('Completá ramo, línea y clase', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        codigoModelo: trimCode(form.codigoModelo),
        descripcion: form.descripcion.trim(),
        codigoRamo: trimCode(form.codigoRamo),
        codigoLinea: trimCode(form.codigoLinea),
        codigoClase: trimCode(form.codigoClase),
        codigoCurva: trimCode(form.codigoCurva),
        codigoTemporada: trimCode(form.codigoTemporada) || null,
        precioBase: Number(form.precioBase) || 0,
        costoBase: Number(form.costoBase) || 0,
      };
      if (isEdit && codigoModelo) {
        const { codigoModelo: _omit, ...rest } = payload;
        void _omit;
        await modeloService.updateModelo(codigoModelo, rest);
        try {
          await persistFotosModelo(codigoModelo);
        } catch (fotoErr: any) {
          const detalle = fotoErr?.response?.data?.message || fotoErr?.message || 'Error al guardar fotos';
          enqueueSnackbar(`Modelo actualizado, pero falló el guardado de fotos: ${detalle}`, { variant: 'warning' });
          return;
        }
        enqueueSnackbar('Modelo actualizado', { variant: 'success' });
      } else {
        await modeloService.createModelo(payload);
        try {
          await persistFotosModelo(payload.codigoModelo);
        } catch (fotoErr: any) {
          const detalle = fotoErr?.response?.data?.message || fotoErr?.message || 'Error al guardar fotos';
          enqueueSnackbar(`Modelo creado, pero falló el guardado de fotos: ${detalle}`, { variant: 'warning' });
          navigate(`/inventario/fashion/modelos/${encodeURIComponent(payload.codigoModelo)}`);
          return;
        }
        enqueueSnackbar('Modelo creado', { variant: 'success' });
        navigate(`/inventario/fashion/modelos/${encodeURIComponent(payload.codigoModelo)}`);
        return;
      }
    } catch (err: any) {
      const msg = typeof err?.response?.data === 'string'
        ? err.response.data
        : (err?.response?.data?.message || 'Error al guardar');
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerar = async () => {
    if (!codigoModelo) {
      enqueueSnackbar('Guardá el modelo antes de generar variantes', { variant: 'warning' });
      return;
    }
    if (selectedColors.length === 0) {
      enqueueSnackbar('Seleccioná al menos un color', { variant: 'warning' });
      return;
    }
    try {
      setGenerating(true);
      const result = await modeloService.generarMatriz(codigoModelo, { codigosColor: selectedColors });
      enqueueSnackbar(`Variantes: ${result.generadas} nuevas, ${result.existentes} existentes`, { variant: 'success' });
      const mat = await modeloService.getMatriz(codigoModelo);
      setMatriz(mat);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data || 'Error al generar variantes', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const coloresSeleccionados = useMemo(
    () => colores.filter((c) => selectedColors.includes(c.codigoColor)),
    [colores, selectedColors],
  );

  const renderSelectorColores = (compact?: boolean) => (
    <Stack spacing={1}>
      <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ fontWeight: 600 }}>
        Colores disponibles del modelo
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Elegí los colores que tendrá este modelo. Luego, en la pestaña Matriz, generá las variantes talle × color.
      </Typography>
      {colores.length === 0 ? (
        <Typography variant="body2" color="warning.main">
          No hay colores cargados. Creá colores en Fashion → Colores.
        </Typography>
      ) : (
        <Autocomplete
          multiple
          options={colores}
          value={coloresSeleccionados}
          disableCloseOnSelect
          getOptionLabel={(o) => `${o.codigoColor} - ${o.nombreColor}`}
          isOptionEqualToValue={(a, b) => a.codigoColor === b.codigoColor}
          onChange={(_, value) => setSelectedColors(value.map((c) => c.codigoColor))}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.codigoColor}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {option.codigoHex && (
                <Box
                  component="span"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: option.codigoHex,
                    border: '1px solid #ccc',
                    mr: 1,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
              )}
              {option.codigoColor} - {option.nombreColor}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.codigoColor}
                size="small"
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {option.codigoHex && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: option.codigoHex,
                          border: '1px solid #ccc',
                        }}
                      />
                    )}
                    <span>{option.nombreColor}</span>
                  </Stack>
                }
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Colores"
              placeholder={selectedColors.length ? '' : 'Buscar y seleccionar colores'}
            />
          )}
        />
      )}
    </Stack>
  );

  if (loading) {
    return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/inventario/fashion/modelos')}
                sx={{ textTransform: 'none' }}
              >
                Modelos
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {isEdit ? 'Editar modelo' : 'Nuevo modelo'}
              </Typography>
            </Stack>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Datos generales" />
              <Tab label="Matriz talle × color" disabled={!isEdit} />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={2} component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Código modelo"
                      fullWidth
                      required
                      disabled={isEdit}
                      value={form.codigoModelo}
                      onChange={(e) => setForm((f) => ({ ...f, codigoModelo: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField
                      label="Descripción"
                      fullWidth
                      required
                      value={form.descripcion}
                      onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Ramo</InputLabel>
                      <Select
                        label="Ramo"
                        value={form.codigoRamo}
                        onChange={(e) => setForm((f) => ({
                          ...f, codigoRamo: e.target.value, codigoLinea: '', codigoClase: '',
                        }))}
                      >
                        {ramos.map((r) => (
                          <MenuItem key={r.codigoRamo} value={trimCode(r.codigoRamo)}>
                            {trimCode(r.codigoRamo)} - {(r.nombreRamo || '').trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Línea</InputLabel>
                      <Select
                        label="Línea"
                        value={form.codigoLinea}
                        onChange={(e) => setForm((f) => ({ ...f, codigoLinea: e.target.value, codigoClase: '' }))}
                      >
                        {lineas.map((l) => (
                          <MenuItem key={l.codigoLinea} value={trimCode(l.codigoLinea)}>
                            {trimCode(l.codigoLinea)} - {(l.nombreLinea || '').trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Clase</InputLabel>
                      <Select
                        label="Clase"
                        value={form.codigoClase}
                        onChange={(e) => setForm((f) => ({ ...f, codigoClase: e.target.value }))}
                      >
                        {clases.map((c) => (
                          <MenuItem key={c.codigoClase} value={trimCode(c.codigoClase)}>
                            {trimCode(c.codigoClase)} - {(c.nombreClase || '').trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Curva de talle</InputLabel>
                      <Select
                        label="Curva de talle"
                        value={form.codigoCurva}
                        onChange={(e) => setForm((f) => ({ ...f, codigoCurva: e.target.value }))}
                      >
                        {curvas.map((c) => (
                          <MenuItem key={c.codigoCurva} value={trimCode(c.codigoCurva)}>
                            {trimCode(c.codigoCurva)} - {(c.nombreCurva || '').trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Temporada</InputLabel>
                      <Select
                        label="Temporada"
                        value={form.codigoTemporada || ''}
                        onChange={(e) => setForm((f) => ({ ...f, codigoTemporada: e.target.value }))}
                      >
                        <MenuItem value="">(Sin temporada)</MenuItem>
                        {temporadas.map((t) => (
                          <MenuItem key={t.codigoTemporada} value={trimCode(t.codigoTemporada)}>
                            {trimCode(t.codigoTemporada)} - {(t.nombreTemporada || '').trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Precio base"
                      type="number"
                      fullWidth
                      value={form.precioBase ?? 0}
                      onChange={(e) => setForm((f) => ({ ...f, precioBase: Number(e.target.value) }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Costo base"
                      type="number"
                      fullWidth
                      value={form.costoBase ?? 0}
                      onChange={(e) => setForm((f) => ({ ...f, costoBase: Number(e.target.value) }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {renderSelectorColores()}
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Fotos por color
                      </Typography>
                      {selectedColors.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Seleccioná al menos un color para cargar fotos. Cada color tiene su propia galería (máx. 4).
                        </Typography>
                      ) : (
                        selectedColors.map((colorCode) => {
                          const color = colores.find((c) => c.codigoColor === colorCode)
                            || { codigoColor: colorCode, nombreColor: colorCode, codigoHex: null as string | null };
                          return (
                            <Stack key={colorCode} spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {color.codigoHex && (
                                  <Box
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      bgcolor: color.codigoHex,
                                      border: '1px solid #ccc',
                                    }}
                                  />
                                )}
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {color.nombreColor} ({colorCode})
                                </Typography>
                              </Stack>
                              <GaleriaFotosSlot
                                ref={(el) => {
                                  galeriaRefs.current[colorCode] = el;
                                }}
                                title={`Fotos · ${color.nombreColor}`}
                                subtitle="Estas fotos se copian a todas las variantes (talles) de este color al guardar o generar."
                                emptyMessage={`Todavía no hay fotos para ${color.nombreColor}.`}
                                inputId={`modelo-fotos-${colorCode}`}
                                initialFotos={fotosPorColor[colorCode] ?? []}
                                disabled={saving}
                              />
                            </Stack>
                          );
                        })
                      )}
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.estado === 'A'}
                          onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? 'A' : 'I' }))}
                        />
                      }
                      label="Activo"
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => navigate('/inventario/fashion/modelos')}>Cancelar</Button>
                  <Button type="submit" variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={22} /> : 'Guardar'}
                  </Button>
                </Stack>
              </Stack>
            )}

            {tab === 1 && isEdit && (
              <Stack spacing={2}>
                {renderSelectorColores(true)}
                <Button
                  variant="contained"
                  onClick={handleGenerar}
                  disabled={generating || selectedColors.length === 0}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {generating ? <CircularProgress size={22} /> : `Generar variantes (${selectedColors.length} color${selectedColors.length === 1 ? '' : 'es'})`}
                </Button>
                {matriz && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Color</TableCell>
                          {tallesHeader.map((t) => (
                            <TableCell key={t.codigoTalle} align="center">{t.codigoTalle}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedColors.length
                          ? selectedColors
                          : Array.from(new Set(matriz.celdas.filter((c) => c.existe).map((c) => c.codigoColor)))
                        ).map((colorCode) => {
                          const nombre = colores.find((c) => c.codigoColor === colorCode)?.nombreColor
                            || matriz.colores.find((c) => c.codigoColor === colorCode)?.nombreColor
                            || colorCode;
                          return (
                            <TableRow key={colorCode}>
                              <TableCell>{nombre}</TableCell>
                              {tallesHeader.map((t) => {
                                const celda = getCelda(colorCode, t.codigoTalle);
                                return (
                                  <TableCell key={t.codigoTalle} align="center" sx={{ bgcolor: celda?.existe ? '#e8f5e9' : undefined }}>
                                    {celda?.existe
                                      ? `${celda.codigoArticulo} (${celda.cantidad ?? 0})`
                                      : '—'}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ModeloForm;
